import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { getLenis } from "@/hooks/useLenis";

export const vimeoSrc = (v) => `https://player.vimeo.com/video/${v.id}?h=${v.h}&title=0&byline=0&portrait=0&color=d4a84d`;

export const VideoLightbox = ({ video, title, kicker, onClose }) => {
  useEffect(() => {
    if (!video) return;
    getLenis()?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { getLenis()?.start(); document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [video, onClose]);

  return (
    <AnimatePresence>
      {video && (
        <motion.div data-testid="video-lightbox" role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-[90] grid place-items-center p-4 bg-[rgba(3,10,12,.86)] backdrop-blur-xl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <button type="button" data-testid="video-lightbox-close" aria-label="Close" onClick={onClose} className="absolute top-4 right-4 z-10 grid place-items-center w-12 h-12 rounded-full border border-white/15 bg-white/5 text-[var(--cream)] hover:border-[var(--gold)] hover:text-[var(--gold-2)] transition-colors"><X size={20} /></button>
          <motion.div data-lenis-prevent onClick={(e) => e.stopPropagation()} className="w-full max-w-[1100px]" initial={{ y: 28, scale: 0.97, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} exit={{ y: 20, scale: 0.98, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 28 }}>
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-black border border-[rgba(212,168,77,.3)] shadow-[0_50px_140px_rgba(0,0,0,.65)]">
              {video.provider === "file" ? (
                <video data-testid="video-lightbox-video" src={video.src} poster={video.poster} title={title} controls autoPlay playsInline preload="auto" className="absolute inset-0 w-full h-full object-contain bg-black" />
              ) : (
                <iframe data-testid="video-lightbox-iframe" src={vimeoSrc(video)} title={title} className="absolute inset-0 w-full h-full" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media" allowFullScreen />
              )}
            </div>
            <div className="flex flex-wrap items-end justify-between gap-4 mt-5 px-1">
              <div>
                {kicker && <span className="block font-mono text-[10px] tracking-[.22em] uppercase text-[var(--gold)]">{kicker}</span>}
                <h3 data-testid="video-lightbox-title" className="m-0 mt-1 font-serif text-2xl sm:text-3xl text-[var(--cream)]">{title}</h3>
              </div>
              {video.provider !== "file" && <a href={`https://vimeo.com/${video.id}/${video.h}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12px] text-[rgba(244,241,234,.55)] hover:text-[var(--gold-2)] transition-colors">Watch on Vimeo <ArrowUpRight size={12} /></a>}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
